import { Controller } from '@hotwired/stimulus';

export class BuddyReorderController extends Controller {
    private dragCard: HTMLElement | null = null;
    private dragPlaceholder: HTMLDivElement | null = null;
    private dragPointerId: number | null = null;
    private dragPointerOffsetY = 0;
    private dragOriginalOrder: string[] = [];
    private suppressNextClick = false;
    private readonly handleDragMoveBound = this.handleDragMove.bind(this);
    private readonly handleDragEndBound = this.handleDragEnd.bind(this);

    disconnect(): void {
        this.teardownDragState();
    }

    beginHandleDrag(event: PointerEvent): void {
        if (event.button !== 0 || this.dragCard) {
            return;
        }

        const handle = event.currentTarget as HTMLElement | null;
        const card = handle?.closest<HTMLElement>('[data-buddy-card]');
        if (!handle || !card) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.activateDrag(card, handle, event.pointerId, event.clientY);
    }

    maybePreventBuddyOpen(event: Event): void {
        if (!this.suppressNextClick) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.suppressNextClick = false;
    }

    stopPropagation(event: Event): void {
        event.stopPropagation();
    }

    private activateDrag(card: HTMLElement, handle: HTMLElement, pointerId: number, pointerY: number): void {
        const buddyList = this.getBuddyList();
        if (!buddyList) {
            return;
        }

        const rect = card.getBoundingClientRect();
        this.dragOriginalOrder = this.getBuddyCards().map((buddyCard) => buddyCard.dataset.relationshipId ?? '');
        this.dragCard = card;
        this.dragPointerId = pointerId;
        this.dragPointerOffsetY = pointerY - rect.top;
        this.suppressNextClick = true;

        const placeholder = document.createElement('div');
        placeholder.className = 'buddy-reorder-placeholder rounded-lg border-2 border-dashed border-green-300 bg-green-50/70';
        placeholder.style.height = `${rect.height}px`;
        this.dragPlaceholder = placeholder;

        card.after(placeholder);
        document.body.append(card);
        card.classList.add('buddy-reorder-card--ready', 'buddy-reorder-card--dragging');
        card.style.width = `${rect.width}px`;
        card.style.height = `${rect.height}px`;
        card.style.left = `${rect.left}px`;
        card.style.top = `${rect.top}px`;
        document.body.classList.add('buddy-reorder-active');
        buddyList.classList.add('buddy-reorder-list--sorting');
        handle.setPointerCapture?.(pointerId);

        document.addEventListener('pointermove', this.handleDragMoveBound);
        document.addEventListener('pointerup', this.handleDragEndBound);
        document.addEventListener('pointercancel', this.handleDragEndBound);
    }

    private handleDragMove(event: PointerEvent): void {
        if (!this.dragCard || !this.dragPlaceholder || this.dragPointerId !== event.pointerId) {
            return;
        }

        event.preventDefault();

        this.dragCard.style.top = `${event.clientY - this.dragPointerOffsetY}px`;
        const cards = this.getBuddyCards().filter((card) => card !== this.dragCard);
        let inserted = false;
        for (const card of cards) {
            const rect = card.getBoundingClientRect();
            const midpoint = rect.top + (rect.height / 2);
            if (event.clientY < midpoint) {
                card.before(this.dragPlaceholder);
                inserted = true;
                break;
            }
        }

        if (!inserted) {
            this.getBuddyList()?.append(this.dragPlaceholder);
        }
    }

    private handleDragEnd(event: PointerEvent): void {
        if (this.dragPointerId !== event.pointerId) {
            return;
        }

        if (!this.dragCard || !this.dragPlaceholder) {
            this.teardownDragState();
            return;
        }

        this.dragPlaceholder.before(this.dragCard);
        this.teardownDragState();
        this.submitReorderedBuddiesIfChanged();
        window.setTimeout(() => {
            this.suppressNextClick = false;
        }, 250);
    }

    private teardownDragState(): void {
        document.removeEventListener('pointermove', this.handleDragMoveBound);
        document.removeEventListener('pointerup', this.handleDragEndBound);
        document.removeEventListener('pointercancel', this.handleDragEndBound);
        document.body.classList.remove('buddy-reorder-active');

        this.getBuddyList()?.classList.remove('buddy-reorder-list--sorting');

        if (this.dragCard) {
            this.dragCard.classList.remove('buddy-reorder-card--ready', 'buddy-reorder-card--dragging');
            this.dragCard.style.removeProperty('width');
            this.dragCard.style.removeProperty('height');
            this.dragCard.style.removeProperty('left');
            this.dragCard.style.removeProperty('top');
        }

        this.dragPlaceholder?.remove();
        this.dragCard = null;
        this.dragPlaceholder = null;
        this.dragPointerId = null;
        this.dragPointerOffsetY = 0;
    }

    private submitReorderedBuddiesIfChanged(): void {
        const reorderForm = this.element.querySelector<HTMLFormElement>('[data-buddy-reorder-form]');
        const inputContainer = this.element.querySelector<HTMLElement>('[data-buddy-reorder-inputs]');
        if (!reorderForm || !inputContainer) {
            return;
        }

        const orderedIds = this.getBuddyCards()
            .map((card) => card.dataset.relationshipId ?? '')
            .filter((value) => value.length > 0);

        if (orderedIds.length === 0 || orderedIds.join(',') === this.dragOriginalOrder.join(',')) {
            return;
        }

        inputContainer.replaceChildren();
        for (const relationshipId of orderedIds) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'relationship_ids';
            input.value = relationshipId;
            inputContainer.append(input);
        }

        reorderForm.requestSubmit();
    }

    private getBuddyList(): HTMLElement | null {
        return this.element.querySelector<HTMLElement>('[data-buddy-reorder-list]');
    }

    private getBuddyCards(): HTMLElement[] {
        return Array.from(this.element.querySelectorAll<HTMLElement>('[data-buddy-card]'));
    }
}
